import React, { useEffect, useRef, useState } from 'react';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';

const GEO_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

const PRIMARY   = new Set([356, 840, 826, 276, 76, 566, 360, 792, 124, 36]);
const SECONDARY = new Set([
  250, 380, 724, 528, 752, 578, 208, 246, 756, 40, 56, 620, 616, 203, 348,
  642, 100, 300, 191, 484, 170, 604, 32, 152, 818, 710, 404, 288, 608, 702,
  458, 764, 704, 50, 144, 586, 524, 554, 682, 784, 634, 414, 364, 368, 400,
  422, 643, 410, 392, 156, 630,
]);

function useCountUp(target, duration = 2000, started = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!started) return;
    const isFloat = target <= 10;
    let startTs = null;
    const ease = (t) => 1 - Math.pow(1 - t, 3);
    const step = (ts) => {
      if (!startTs) startTs = ts;
      const p = Math.min((ts - startTs) / duration, 1);
      setCount(isFloat ? +(target * ease(p)).toFixed(1) : Math.floor(target * ease(p)));
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [started, target, duration]);
  return count;
}

const SocialProof = () => {
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef(null);

  const students  = useCountUp(23009, 2000, visible);
  const ratings   = useCountUp(2022,  1800, visible);
  const countries = useCountUp(146,   1600, visible);
  const rating    = useCountUp(4.6,   1400, visible);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVisible(true); },
      { threshold: 0.05 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const cls = (base) => `${base} ${visible ? 'sp-in' : 'sp-out'}`;

  return (
    <section className="sp-section" ref={sectionRef}>
      <div className="sp-bg-blue" />
      <div className="sp-bg-purple" />
      <div className="sp-bg-lime" />

      <div className="sp-inner">

        {/* Header */}
        <div className={cls('sp-header')}>
          <h2 className="sp-title">Learners Across The Globe</h2>
          <p className="sp-subtitle">
            Over 23,000 engineers worldwide trust us to sharpen their system design skills
            and walk into FAANG interviews with confidence.
          </p>
        </div>

        {/* Bento */}
        <div className={cls('sp-bento sp-bento-delay')}>

          {/* MAP — left */}
          <div className="sp-card sp-map-card">
            <div className="sp-map-glow" />
            <div className="sp-map-badge">
              <span className="sp-map-num">{countries}+</span>
              <span className="sp-map-lbl">Countries</span>
            </div>
            <ComposableMap
              projection="geoNaturalEarth1"
              projectionConfig={{ scale: 145, center: [10, 5] }}
              style={{ width: '100%', height: '100%' }}
            >
              <Geographies geography={GEO_URL}>
                {({ geographies }) =>
                  geographies.map((geo) => {
                    const id = parseInt(geo.id, 10);
                    const isPrimary   = PRIMARY.has(id);
                    const isSecondary = SECONDARY.has(id);
                    const fill = isPrimary
                      ? 'rgba(192,255,0,0.65)'
                      : isSecondary
                      ? 'rgba(96,165,250,0.3)'
                      : 'rgba(255,255,255,0.04)';
                    const stroke = isPrimary
                      ? 'rgba(192,255,0,0.25)'
                      : isSecondary
                      ? 'rgba(96,165,250,0.15)'
                      : 'rgba(255,255,255,0.06)';
                    return (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        fill={fill}
                        stroke={stroke}
                        strokeWidth={0.5}
                        style={{
                          default: { outline: 'none' },
                          hover:   { outline: 'none', fill },
                          pressed: { outline: 'none' },
                        }}
                      />
                    );
                  })
                }
              </Geographies>
            </ComposableMap>
            <div className="sp-map-fade" />
          </div>

          {/* RIGHT — two cards filling exact same height as map */}
          <div className="sp-right">

            {/* Students card */}
            <div className="sp-card sp-stat-card">
              {/* Icon + number inline */}
              <div className="sp-stat-top">
                <div className="sp-icon-wrap sp-icon-blue">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                    <path d="M12 3L1 9l11 6 9-4.91V17h2V9L12 3z" fill="#c0ff00"/>
                    <path d="M5 13.18V17.18L12 21l7-3.82v-4L12 17l-7-3.82z" fill="rgba(192,255,0,0.55)"/>
                  </svg>
                </div>
                <div>
                  <div className="sp-stat-num sp-lime">{students.toLocaleString()}</div>
                  <div className="sp-stat-label">Students Enrolled</div>
                </div>
              </div>
              <div className="sp-stat-divider" />
              <div className="sp-stat-details">
                <div className="sp-detail-item">
                  <span className="sp-detail-dot" style={{ background: '#c0ff00' }} />
                  <span>Across all courses on the platform</span>
                </div>
                <div className="sp-detail-item">
                  <span className="sp-detail-dot" style={{ background: '#c0ff00' }} />
                  <span>New learners joining every week</span>
                </div>
                <div className="sp-detail-item">
                  <span className="sp-detail-dot" style={{ background: '#c0ff00' }} />
                  <span>From students to senior engineers</span>
                </div>
              </div>
              <div className="sp-card-bar sp-bar-lime" />
            </div>

            {/* Rating card */}
            <div className="sp-card sp-stat-card">
              <div className="sp-stat-top">
                <div className="sp-icon-wrap sp-icon-purple">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
                      fill="#a78bfa"/>
                  </svg>
                </div>
                <div>
                  <div className="sp-stat-num sp-purple">
                    {visible ? rating.toFixed(1) : '0.0'}<span className="sp-star-suffix">★</span>
                  </div>
                  <div className="sp-stat-label">Average Rating</div>
                </div>
              </div>
              <div className="sp-stat-divider" />
              <div className="sp-stat-details">
                <div className="sp-detail-item">
                  <span className="sp-detail-dot" style={{ background: '#a78bfa' }} />
                  <span>{ratings.toLocaleString()} verified ratings</span>
                </div>
                <div className="sp-detail-item">
                  <span className="sp-detail-dot" style={{ background: '#a78bfa' }} />
                  <span>Consistently rated top in category</span>
                </div>
                <div className="sp-detail-item">
                  <span className="sp-detail-dot" style={{ background: '#a78bfa' }} />
                  <span>Loved by engineers at FAANG &amp; beyond</span>
                </div>
              </div>
              <div className="sp-card-bar sp-bar-purple" />
            </div>

          </div>
        </div>
      </div>
    </section>
  );
};

export default SocialProof;
